import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { cn } from '@muzly/lib/utils';

import { isFullscreenPath } from './fullscreenPaths';
import { TabHomeIcon, TabMoreIcon } from '../components/icons';

/**
 * 탭 셸 — 홈·MY가 공유하는 하단 탭바(시안 `1014:9946` 하단 `홈`/`MY`).
 *
 * 시안의 탭바는 **풀 너비**다: 위에 1px 구분선(`#ededf0`), 흰 바탕, 아이콘 24px 위에
 * 12px 라벨. 활성 탭은 브랜드 레드, 비활성은 `#9ca1ab`. 알약형이 아니다.
 *
 * 진단·이력·회차 상세는 탭바를 **감춘다**. 진단은 몰입 화면이라 나가는 문이 X 하나여야
 * 하고, 탭바를 깔면 측정 중에 화면을 갈아탈 두 번째 문이 생긴다. 초기 설정·디버그만 이
 * 셸 밖이다.
 *
 * 감추되 라우트는 이 셸 **안**에 둔다(`AppRoutes`). 밖에 두면 진단으로 들어갈 때 탭
 * 서브트리가 통째로 unmount되고, 그 상태로 View Transitions 스냅샷이 잡혀 WKWebView에서
 * 깜빡인다 (`epyt-page-transition-reconciler-fix`). 그래서 아래 JSX는 두 모드가 **같은
 * 루트·같은 위치의 `<Outlet>`** 을 쓰고 탭바와 하단 패딩만 조건부다 — reconciler가 셸을
 * 같은 fiber로 유지하도록.
 *
 * 네이티브 셸이 실어 주는 `--safe-area-bottom`만큼 띄우고, 값이 없는 브라우저에서는
 * `env(safe-area-inset-bottom)`으로 떨어진다. 본문 하단 패딩은 **바 높이 + 같은 inset**이라
 * 마지막 요소가 바 밑으로 숨지 않는다.
 */

const TABS = [
  // 홈은 `end` — 아니면 모든 경로가 홈의 하위로 잡혀 두 탭이 같이 켜진다.
  { to: '/', icon: TabHomeIcon, labelKey: 'web.tabs.home', end: true },
  // MY 는 `end`가 아니다 — `/more/*`에서도 MY 탭이 켜져 있어야 한다.
  { to: '/more', icon: TabMoreIcon, labelKey: 'web.tabs.more', end: false },
];

/**
 * 탭바가 화면 끝에서 띄우는 만큼 — 홈 인디케이터 위.
 *
 * 화면이 자기 것을 바닥에 띄울 때도(프로필의 저장 바) 같은 값을 쓴다 — 두 벌로
 * 적으면 한쪽만 고쳐질 자리다. 탭바 없는 전체화면의 CTA 는 2rem 바닥을 얹은
 * `CTA_BOTTOM_PAD` 를 쓴다.
 */
export const BOTTOM_INSET =
  'calc(var(--safe-area-bottom, env(safe-area-inset-bottom, 0px)) + 0.75rem)';
/**
 * 탭바 없는 전체화면(온보딩·진단)의 하단 CTA 여백. 시안의 2rem 을 바닥으로 두고, 그보다 큰
 * inset 이 오면 그만큼 띄운다 — Android(targetSdk 36)는 edge-to-edge 라 WebView 가 3버튼
 * 내비게이션 바 밑까지 그려져, 고정 `pb-8` 이면 버튼이 바에 가렸다(2026-09-23 실기기).
 */
export const CTA_BOTTOM_PAD = `max(2rem, ${BOTTOM_INSET})`;
/** 탭바 높이(구분선 + 아이콘 + 라벨 ≈ 3rem) + 여유 — 본문이 바 뒤로 밀리지 않게. */
const CONTENT_PAD = `calc(${BOTTOM_INSET} + 3.5rem)`;

export const TabLayout = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const immersive = isFullscreenPath(pathname);

  return (
    <div className="flex min-h-dvh flex-col bg-background text-foreground">
      {/* 랜드마크 하나 — 스크린리더가 "본문으로" 갈 자리가 없어 화면 전체가 랜드마크
          밖 영역으로 읽혔다(axe `landmark-one-main`·`region`). */}
      <main
        className="flex flex-1 flex-col"
        style={{ paddingBottom: immersive ? undefined : CONTENT_PAD }}
      >
        <Outlet />
      </main>

      {/* 감출 때도 렌더는 유지한다 — 트리 모양이 두 모드에서 같아야 reconciler가
          셸을 같은 fiber로 본다(위 주석). `flex`/`hidden`은 둘 다 display라 배타로 쓰고,
          `hidden` 속성을 같이 걸어 접근성 트리·탭 순서에서도 빠지게 한다. */}
      <nav
        hidden={immersive}
        className={cn(
          'fixed inset-x-0 bottom-0 flex-col border-t border-hairline bg-white',
          immersive ? 'hidden' : 'flex',
        )}
        style={{ paddingBottom: BOTTOM_INSET }}
      >
        <div className="flex items-start justify-center gap-8 pt-2">
          {TABS.map(({ to, icon: Icon, labelKey, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  // 44px 터치 타깃. 시안의 글자 크기·간격은 그대로 두고 높이만 채운다.
                  'flex min-h-[44px] w-[94px] flex-col items-center justify-center gap-1 text-[11px] leading-none tracking-[-0.06px]',
                  isActive
                    ? 'font-semibold text-primary'
                    : // 비활성 라벨이 11px 에 2.59:1 이었다(axe). 토큰 계단은 시안이
                      // 정본이라 값을 바꾸지 않고 **더 진한 칸**(bk-700, 7.0:1)을 쓴다.
                      'font-medium text-bk-700',
                )
              }
            >
              <Icon className="h-6 w-6" aria-hidden="true" />
              {t(labelKey)}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};
